import React from 'react'
import styles from './pupil-info-form.styles'
import injectSheet from 'react-jss'
import Row from './row/row'
import Cell from './cell/cell'
import HintCell from './hint-cell/hint-cell'
import FormLabel from 'pobble-components/dist/form-elements/form-label'
import FormInput from 'pobble-components/dist/form-elements/form-input'
import CharactersCount from 'pobble-components/dist/form-elements/characters-count'
import AgeGroupsSelect from '../../../../../age-groups-select/age-groups-select.container'
import Button from 'pobble-components/dist/button'
import { Formik } from 'formik'
import UpdatingOverlay from './updating-overlay/updating-overlay'
import * as Yup from 'yup'
import Groups from './groups/groups.container'
import { validateDisplayName } from 'pobble-components/dist/validators'

const DISPLAY_NAME_CHARACTERS_LIMIT = 20

const PupilInfoForm = ({
  id,
  classes,
  firstName,
  lastName,
  displayName,
  bdMonth,
  bdDay,
  ageGroup,
  updatePupil,
  showUpdatingOverlay,
  passCode,
  parentCode,
  parents,
  onResetPasscode,
  onAddToGroup,
  editable,
  permission,
  editableProps
}) => (
  <Formik
    enableReinitialize={true}
    onSubmit={values => updatePupil(values)}
    initialValues={{
      firstName: firstName,
      lastName: lastName,
      displayName: displayName || '',
      bdMonth: bdMonth,
      bdDay: bdDay,
      ageGroup: ageGroup
    }}
    validationSchema={Yup.object().shape({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      displayName: validateDisplayName().required('Display name is required')
    })}>
    {props => {
      const {
        initialValues,
        values,
        handleChange,
        handleSubmit,
        handleBlur,
        errors,
        isValid
      } = props
      return (
        <form onSubmit={handleSubmit} className={classes.container}>
          {showUpdatingOverlay && (
            <UpdatingOverlay className={classes.updatingOverlay} />
          )}
          <div className={classes.submitHolder}>
            <span>
              {firstName}
              's info
            </span>
            <Button
              disabled={!isValid || values === initialValues}
              type="submit"
              tall
              secondary>
              Save changes
            </Button>
          </div>
          <Row>
            <Cell>
              <FormLabel htmlFor="firstName">First Name</FormLabel>
              <FormInput
                disabled={!editableProps.first_name}
                validationError={errors.firstName}
                placeholder="e.g. William"
                id="firstName"
                name="firstName"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.firstName}
              />
            </Cell>
            <Cell>
              <FormLabel htmlFor="lastName">Last Name</FormLabel>
              <FormInput
                disabled={!editableProps.last_name}
                validationError={errors.lastName}
                placeholder="e.g. Shakespeare"
                id="lastName"
                name="lastName"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.lastName}
              />
            </Cell>
            <HintCell>
              Only visible to the teachers and administrators of your school,
              and the pupil’s parents
            </HintCell>
          </Row>
          <Row>
            <Cell>
              <FormLabel htmlFor="displayName" spaceBetween>
                Display name
                <CharactersCount
                  value={values.displayName || ''}
                  max={DISPLAY_NAME_CHARACTERS_LIMIT}
                />
              </FormLabel>
              <FormInput
                disabled={!editableProps.display_name}
                name="displayName"
                id="displayName"
                validationError={errors.displayName}
                placeholder="e.g. Will"
                maxLength={DISPLAY_NAME_CHARACTERS_LIMIT}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.displayName}
              />
            </Cell>
            <Cell />
            <HintCell>
              The pupil chooses a public display name. Please avoid personally
              identifiable information, like last names.{' '}
            </HintCell>
          </Row>
          <Row>
            <Cell>
              <FormLabel htmlFor="pupil-profile-info-age-groups">
                Age group
              </FormLabel>
              <AgeGroupsSelect
                formSelectProps={{
                  name: 'ageGroup',
                  onBlur: handleBlur,
                  onChange: handleChange,
                  value: values.ageGroup,
                  disabled: !editable
                }}
              />
            </Cell>
            <Cell />
            <HintCell>
              The age group is used to tag work, and is public.
            </HintCell>
          </Row>
          <Row>
            <Cell>
              <FormLabel>Groups:</FormLabel>
              <Groups />
            </Cell>
            <Cell className={classes.groupAddCell}>
              <Button
                action={onAddToGroup}
                className={classes.groupAddButton}
                tall
                secondary>
                Add to a group
              </Button>
            </Cell>
            <HintCell className={classes.groupAddHintCell}>
              Groups can be used to segment and easily find students.
              <br />
              Public groups are visible on your school page.
            </HintCell>
          </Row>
          <Row>
            <Cell>
              <FormLabel>Pupil passcode:</FormLabel>
              <FormInput disabled value={passCode} />
            </Cell>
            <Cell>
              {editable && (
                <Button
                  action={onResetPasscode}
                  tall
                  type="button"
                  secondary
                  className={classes.resetPass}>
                  Reset passcode
                </Button>
              )}
            </Cell>
            <HintCell>
              Provide to child so they can login to their Pobble account
            </HintCell>
          </Row>
          <Row>
            <Cell>
              <FormLabel>Parent code:</FormLabel>
              <FormInput disabled value={parentCode || '-'} />
            </Cell>
            <Cell>
              <FormLabel>Connected parents:</FormLabel>
              <FormInput
                disabled
                value={
                  parents &&
                  parents.map(parent => parent.account.full_name + ' ' || '-')
                }
              />
            </Cell>
            <HintCell>
              Provide this unique Parent code to this child’s parent / carer so
              they can connect with their child’s Pobble account.
            </HintCell>
          </Row>
        </form>
      )
    }}
  </Formik>
)

export default injectSheet(styles)(PupilInfoForm)
